import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class PostMessageService {
  private readonly ALLOWED_ORIGINS = ['*']; // Can be restricted to specific origins if needed
  private messageIdCounter = 0;

  /**
   * Check if we're running in a WebView/iframe context
   */
  isInWebView(): boolean {
    return (
      this.isNativeAppWebView() ||
      window.parent !== window
    );
  }

  /**
   * Accurate native app WebView detection (not iframes)
   */
  isNativeAppWebView(): boolean {
    const ua = navigator.userAgent || '';

    const isAndroid = /Android/i.test(ua);
    const hasWvToken = /\bwv\)/i.test(ua);
    const isAndroidWebView =
      isAndroid && (hasWvToken || (/Version\/\d+/i.test(ua) && !/Chrome\/\d+/i.test(ua)));

    const isIOS = /iP(ad|hone|od)/i.test(ua);
    const isSafari =
      isIOS &&
      /Safari/i.test(ua) &&
      !/CriOS|FxiOS|OPiOS|EdgiOS/i.test(ua);
    const isIOSWebView = isIOS && !isSafari;

    return isAndroidWebView || isIOSWebView;
  }

  /**
   * Check if a native bridge (Flutter, React Native, etc.) is available
   */
  hasNativeBridge(): boolean {
    return !!(
      (window as any).flutter_inappwebview ||
      (window as any).FlutterChannel ||
      (window as any).LoginChannel ||
      (window as any).OAuthChannel ||
      (window as any).AppChannel
    );
  }

  /**
   * Try to send a message via native bridge.
   * Returns true if message was sent successfully via a bridge.
   */
  private sendViaNativeBridge(type: string, message: any): boolean {
    const serialized = JSON.stringify(message);

    // flutter_inappwebview
    const fiiw = (window as any).flutter_inappwebview;
    if (fiiw?.callHandler) {
      try {
        fiiw.callHandler('onMessage', message);
        return true;
      } catch (e) {
        console.warn('[PostMessage] flutter_inappwebview.callHandler failed:', e);
      }
    }

    // webview_flutter JavaScriptChannel
    const channelNames = ['LoginChannel', 'OAuthChannel', 'AppChannel', 'FlutterChannel'];
    for (const name of channelNames) {
      const channel = (window as any)[name];
      if (channel?.postMessage) {
        try {
          channel.postMessage(serialized);
          return true;
        } catch (e) {
          console.warn(`[PostMessage] ${name}.postMessage failed:`, e);
        }
      }
    }

    return false;
  }

  /**
   * Get the target window for postMessage
   */
  private getTargetWindow(): Window {
    return window.parent && window.parent !== window ? window.parent : window;
  }

  /**
   * Generate unique message ID
   */
  private generateMessageId(): string {
    return `msg_${Date.now()}_${++this.messageIdCounter}`;
  }

  /**
   * Send postMessage with standardized format
   * 
   * Cross-Origin-Opener-Policy (COOP) Notes:
   * - This method wraps postMessage in try-catch to handle COOP gracefully
   * - COOP warnings may appear in console but are harmless - messages still work
   * - For production, ensure server sets: Cross-Origin-Opener-Policy: same-origin-allow-popups
   * - This allows popup communication while maintaining security isolation
   * 
   * @param type - Message type (e.g., 'APP_LOGIN_REQUEST')
   * @param data - Message payload
   * @param origin - Target origin (default: '*' for any origin)
   */
  private sendMessage(type: string, data: any, origin: string = '*'): void {
    const safeData = data || {};
    const message = {
      type,
      timestamp: Date.now(),
      messageId: this.generateMessageId(),
      source: 'web-widget',
      data: safeData,
      ...(typeof safeData === 'object' ? safeData : {}),
    };

    // Try native bridge first (Flutter, etc.) when in a native app WebView
    if (this.isNativeAppWebView()) {
      const sentViaBridge = this.sendViaNativeBridge(type, message);
      if (sentViaBridge) {
        console.log(`[PostMessage] Sent ${type} via native bridge`);
        return;
      }
    }

    // Fallback to window.postMessage
    const targetWindow = this.getTargetWindow();
    const shouldLog = !this.isInWebView() || 
                      (window as any).__DEBUG_POSTMESSAGE__ || 
                      type === 'APP_LOGIN_REQUEST' || 
                      type === 'APP_LOGOUT_REQUEST' ||
                      type === 'APP_LOGIN_SUCCESS' ||
                      type === 'APP_LOGOUT_SUCCESS';
    if (shouldLog) {
      console.log(`[PostMessage] Sending ${type}:`, message);
    }
    
    try {
      targetWindow.postMessage(message, origin);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('Cross-Origin-Opener-Policy')) {
        if ((window as any).__DEBUG_POSTMESSAGE__) {
          console.warn(
            `[PostMessage] COOP warning for ${type}. This is expected behavior. ` +
            `Message may still be delivered. Ensure server sets COOP header.`
          );
        }
      } else {
        if ((window as any).__DEBUG_POSTMESSAGE__) {
          console.warn(`[PostMessage] Error sending ${type}:`, error);
        }
      }
    }
  }

  /**
   * Send login request when user clicks login button
   */
  sendLoginRequest(provider: 'google' | 'facebook'): void {
    this.sendMessage('APP_LOGIN_REQUEST', {
      provider,
      action: 'login',
    });
  }

  /**
   * Send user info after successful login
   */
  sendLoginSuccess(userInfo: {
    provider: 'google' | 'facebook';
    userId: string;
    email?: string;
    userName?: string;
    profilePicture?: string;
    accessToken?: string;
  }): void {
    this.sendMessage('APP_LOGIN_SUCCESS', {
      ...userInfo,
      isLoggedIn: true,
      timestamp: Date.now(),
    });
  }

  /**
   * Send logout request when user clicks logout
   */
  sendLogoutRequest(provider: 'google' | 'facebook'): void {
    this.sendMessage('APP_LOGOUT_REQUEST', {
      provider,
      action: 'logout',
    });
  }

  /**
   * Send logout success confirmation
   */
  sendLogoutSuccess(): void {
    this.sendMessage('APP_LOGOUT_SUCCESS', {
      isLoggedIn: false,
      timestamp: Date.now(),
    });
  }

  /**
   * Send error message
   */
  sendError(error: {
    type: string;
    message: string;
    provider?: 'google' | 'facebook';
  }): void {
    this.sendMessage('APP_ERROR', {
      ...error,
      timestamp: Date.now(),
    });
  }

  /**
   * Send status update (for periodic status checks)
   */
  sendStatusUpdate(status: {
    isLoggedIn: boolean;
    provider?: 'google' | 'facebook';
    userId?: string;
  }): void {
    this.sendMessage('APP_STATUS_UPDATE', {
      ...status,
      timestamp: Date.now(),
    });
  }
}

